"use client";

// src/context.tsx
import * as React from "react";
var ProgressContext = React.createContext(null);
var useProgressContext = () => {
  const context = React.useContext(ProgressContext);
  return context;
};

// src/hooks.ts
import * as React2 from "react";
import { valueToPercent, clampValue } from "@stepstone-genesis/utilities";
import { useToggle } from "@stepstone-genesis/hooks-use-toggle";
import { mergeRefs } from "@stepstone-genesis/hooks-use-merged-refs";
import { useUID } from "@stepstone-genesis/uid-library";
var useProgressState = (props = {}) => {
  const {
    value = 0,
    min = 0,
    max = 100,
    isIndeterminate = false,
    id: identifier,
    a11yLabel = "progress bar",
    isLabelVisible: isLabelVisibleProp = false
  } = props;
  const uuid = useUID();
  const id = identifier != null ? identifier : `stepstone-progress-label-${uuid}`;
  const determinedValue = isIndeterminate ? 0 : value || 0;
  const clampedValue = clampValue(determinedValue, min, max);
  const percent = valueToPercent(clampedValue, min, max);
  const [isLabelVisible, { on: toggleLabel }] = useToggle(isLabelVisibleProp);
  const progressLabelProps = React2.useCallback(
    (labelProps = {}, forwardedRef = null) => {
      var _a;
      return {
        id: (_a = labelProps.id) != null ? _a : id,
        ...labelProps,
        ref: mergeRefs(forwardedRef, (element) => {
          if (!element)
            return;
          toggleLabel();
        })
      };
    },
    [id, toggleLabel]
  );
  return {
    value: clampedValue,
    min,
    max,
    percent,
    isIndeterminate,
    isLabelVisible,
    progressLabelProps,
    props: {
      role: "progressbar",
      "aria-label": a11yLabel,
      "data-indeterminate": isIndeterminate != null ? isIndeterminate : void 0,
      "aria-valuemax": max,
      "aria-valuemin": min,
      "aria-valuenow": isIndeterminate ? void 0 : determinedValue,
      "aria-valuetext": isIndeterminate ? "indeterminate" : determinedValue.toString(),
      ...isLabelVisible ? { "aria-describedby": id } : {}
    }
  };
};

// src/state-handler.tsx
import * as React3 from "react";
var ProgressStateHandler = ({ children, ...props }) => {
  const context = useProgressState(props);
  const formStateContext = React3.useMemo(() => context, [context]);
  return /* @__PURE__ */ React3.createElement(ProgressContext.Provider, { value: formStateContext }, children);
};
var Progress = React3.memo(
  ProgressStateHandler
);
export {
  Progress,
  ProgressContext,
  ProgressStateHandler,
  useProgressContext,
  useProgressState
};
